import { Typography } from "@foodpilot/foods";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { isEqual } from "lodash";

import type { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import { useUpsertIngredient } from "src/api/client-api/ingredients/useUpsertIngredient";
import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { AssociatedStructureWarning } from "src/components/Dialog/Templates/AllWarnings/AssociatedStructureWarning";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import {
    DataDeletionWarningDialog,
    type ConfirmationModalData,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import {
    EntityFormPage,
    EntityUnsavedChangesDialog,
    EntityValidationBanner,
    FormHeader,
} from "src/components/EntityForm";
import { useEntityFormLeaveGuard } from "src/components/EntityForm/useEntityFormLeaveGuard";
import { useSaveIngredientSupplierTransports } from "src/components/Transport/IngredientSupplierTransportFields";
import { hasMissingRequiredIngredientSupplierTransportSelections } from "src/components/Transport/ingredientSupplierTransportPersistence";
import { useIngredientSupplierTransportSubmission } from "src/components/Transport/useIngredientSupplierTransportSubmission";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { IngredientService } from "src/pages/Ingredient/IngredientService";
import { URLHandler } from "src/services/URLHandler";
import { useSnackbar } from "src/utils/useSnackbar";

import type { IngredientFormType } from "../../IngredientForm.types";
import { ingredientFormSchema, type IngredientFormData } from "../../IngredientForm.schema";
import { IngredientIdentitySection } from "./IngredientIdentitySection";
import { IngredientOrganizationSection } from "./IngredientOrganizationSection";
import { IngredientReferenceScorePreview } from "./IngredientReferenceScorePreview";
import { IngredientSuppliersSection } from "./IngredientSuppliersSection";

type IngredientCreationV2Props = {
    existingIngredient?: Ingredient;
};

const makeInitialIngredient = (
    existingIngredient: Ingredient | undefined,
    selectedCompany: ReturnType<typeof useFoodpilotContext>["selectedCompany"],
): IngredientFormType => {
    const draft = IngredientService.getIngredientToCreate(existingIngredient);
    if (existingIngredient !== undefined || selectedCompany === null) return draft;

    return {
        ...draft,
        ownedBy: {
            "@id": selectedCompany["@id"],
            id: selectedCompany.id,
            name: selectedCompany.name,
        },
    };
};

/** Long-scroll Ingredient V2 creation flow on the shared entity-form architecture. */
export const IngredientCreationV2 = ({ existingIngredient }: IngredientCreationV2Props) => {
    if (existingIngredient !== undefined) {
        return <IngredientCreationV2Content existingIngredient={existingIngredient} />;
    }

    return (
        <EntityScoresProvider entity="ingredient">
            <IngredientCreationV2Content />
        </EntityScoresProvider>
    );
};

const IngredientCreationV2Content = ({ existingIngredient }: IngredientCreationV2Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isUserOfTypeSupplier, selectedCompany } = useFoodpilotContext();
    const snackbar = useSnackbar();
    const upsertIngredient = useUpsertIngredient();
    const saveIngredientSupplierTransports = useSaveIngredientSupplierTransports();
    const { isStageEnabled } = useEnabledTransportStages();
    const isNew = existingIngredient === undefined;
    const formTitle =
        isNew ?
            t("ingredient.form.v2.creation_title", "New ingredient")
        :   t("ingredient.form.modification_title", "Modify the ingredient");
    const [ingredient, setIngredientState] = useState(() => makeInitialIngredient(existingIngredient, selectedCompany));
    const initialIngredientRef = useRef(ingredient);
    const [warningModal, setWarningModal] = useState<ConfirmationModalData>({ isOpen: false, errors: null });
    const [validationAttempt, setValidationAttempt] = useState(0);
    const validationBannerRef = useRef<HTMLDivElement>(null);
    const saveForExitRef = useRef(false);
    const warningConfirmedRef = useRef(false);
    const initialOrganizations = useRef(ingredient.shares.map(({ organization }) => organization));

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<IngredientFormData>({
        resolver: zodResolver(ingredientFormSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: ingredient.name ?? "",
            clientId: ingredient.clientId ?? "",
            ingredientReferenceId: ingredient.ingredientReferenceId ?? undefined,
            organic: ingredient.organic ?? undefined,
            ownedBy: ingredient.ownedBy ?? undefined,
            ingredientLine: ingredient.ingredientLine ?? undefined,
            tags: ingredient.tags ?? {},
        },
    });

    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const defaultIngredientListUrl = URLHandler.ingredient.list(selectedCompany?.id ?? 0);
    const ingredientListUrl = returnUrl ? decodeURIComponent(returnUrl) : defaultIngredientListUrl;
    const enabledIngredientStages = INGREDIENT_LEVEL_STAGES.filter((stage) => isStageEnabled(stage));
    const showSuppliers = !isUserOfTypeSupplier();
    const sectionTotal = showSuppliers ? 3 : 2;

    useEffect(() => {
        if (validationAttempt === 0) return;
        validationBannerRef.current?.focus({ preventScroll: true });
        validationBannerRef.current?.scrollIntoView?.({ block: "start" });
    }, [validationAttempt]);

    const notifyPersistenceError = () => {
        snackbar.setSnackbarProps({
            type: "error",
            message: t("une_erreur_est_survenue", "An error occurred"),
        });
    };
    const notifyTransportError = () => {
        snackbar.setSnackbarProps({
            type: "error",
            message: t(
                "ingredientSupplier.transport.saveError",
                "The transport circuits could not be saved. Please try again.",
            ),
        });
    };
    const submission = useIngredientSupplierTransportSubmission({
        draft: ingredient,
        setDraftState: setIngredientState,
        relationsKey: "suppliers",
        relatedResourceKey: "supplier",
        getDraftCounterpartIri: (row) => row.supplier["@id"],
        upsert: (draft) => {
            if (selectedCompany === null) {
                return Promise.reject(new Error("No selected company."));
            }

            return upsertIngredient.mutateAsync({
                ingredient: IngredientService.ingredientFormToIngredientWrite(draft, selectedCompany["@id"]),
                companyId: selectedCompany.id,
            });
        },
        saveTransportSelections: saveIngredientSupplierTransports,
        onSuccess: (persistedDraft) => {
            if (saveForExitRef.current) {
                saveForExitRef.current = false;
                leaveGuard.completeSave(ingredientListUrl);
                return;
            }
            if (persistedDraft.id !== undefined && selectedCompany !== null) {
                navigate(URLHandler.ingredient.scores(selectedCompany.id, persistedDraft.id));
                return;
            }

            navigate(ingredientListUrl);
        },
        onExit: () => undefined,
        onPersistenceError: notifyPersistenceError,
        onTransportError: notifyTransportError,
        onConcurrentEdit: notifyPersistenceError,
    });
    const leaveGuard = useEntityFormLeaveGuard({
        dirty: !isEqual(ingredient, initialIngredientRef.current),
        busy: submission.isSubmitting || upsertIngredient.isLoading,
    });

    if (selectedCompany === null) {
        return (
            <Typography variant="big-bold">
                {t("ingredient.form.page_not_found", "The page you are trying to access does not exist.")}
            </Typography>
        );
    }

    const submitIngredient = async () => {
        const didSave = await submission.submit();
        if (!didSave && saveForExitRef.current) {
            saveForExitRef.current = false;
            leaveGuard.cancelSave();
        }
    };
    const saveValidIngredient = async () => {
        if (hasMissingRequiredIngredientSupplierTransportSelections(ingredient.suppliers, enabledIngredientStages)) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t(
                    "ingredientSupplier.transport.missingRequiredSuppliers",
                    "Enter the required transport circuits for each supplier.",
                ),
            });
            if (saveForExitRef.current) {
                saveForExitRef.current = false;
                leaveGuard.cancelSave();
            }
            return;
        }

        const warnings: ReactNode[] = [];
        const initialReferenceId = existingIngredient?.ingredientReferenceId;
        if (initialReferenceId !== undefined && initialReferenceId !== ingredient.ingredientReferenceId) {
            warnings.push(<SsqPostChangedWarnings key="reference-change" />);
        }

        const currentOrganizationIds = new Set(ingredient.shares.map(({ organization }) => organization.id));
        const deletedOrganizations = initialOrganizations.current
            .filter((organization) => !currentOrganizationIds.has(organization.id))
            .map((organization) => organization.name)
            .join(",");
        if (deletedOrganizations.length > 0) {
            warnings.push(
                <AssociatedStructureWarning key="organization-change" deletedEntities={deletedOrganizations} />,
            );
        }

        if (warnings.length > 0) {
            setWarningModal({ isOpen: true, errors: warnings });
            return;
        }

        await submitIngredient();
    };
    const save = handleSubmit(saveValidIngredient, () => {
        setValidationAttempt((current) => current + 1);
        if (saveForExitRef.current) {
            saveForExitRef.current = false;
            leaveGuard.cancelSave();
        }
    });

    return (
        <>
            <DataDeletionWarningDialog
                confirmationData={warningModal}
                setConfirmationData={(next) => {
                    setWarningModal(next);
                    if (!next.isOpen && saveForExitRef.current && !warningConfirmedRef.current) {
                        saveForExitRef.current = false;
                        leaveGuard.cancelSave();
                    }
                }}
                onConfirm={() => {
                    warningConfirmedRef.current = true;
                    void submitIngredient().finally(() => {
                        warningConfirmedRef.current = false;
                    });
                }}
            />
            <EntityFormPage
                header={(ref) => (
                    <FormHeader
                        ref={ref}
                        breadcrumbs={[
                            {
                                label: t("ingredient.form.breadcrumb.list", "All ingredients"),
                                action: () => leaveGuard.requestExit(ingredientListUrl),
                            },
                            {
                                label: formTitle,
                            },
                        ]}
                        metadata={
                            ingredient.ownedBy === null ?
                                undefined
                            :   {
                                    label: t("product.v2.informations.owning_entity", "Owning entity:"),
                                    value: ingredient.ownedBy.name,
                                }
                        }
                        title={formTitle}
                        onCancel={() => leaveGuard.requestExit(defaultIngredientListUrl)}
                        onSave={() => void save()}
                        saving={submission.isSubmitting || upsertIngredient.isLoading}
                        testIdPrefix="ingredient-v2"
                    />
                )}
                aside={({ isNarrow, stickyTop }) => (
                    <IngredientReferenceScorePreview
                        ingredientReferenceId={ingredient.ingredientReferenceId}
                        referenceSource={ingredient.referenceSource}
                        isNarrow={isNarrow}
                        stickyTop={stickyTop}
                    />
                )}
            >
                <EntityValidationBanner
                    bannerRef={validationBannerRef}
                    entityLabel={t("entity_form.entity.ingredient", "ingredient")}
                    issueCount={Object.keys(errors).length}
                />
                <IngredientIdentitySection
                    control={control}
                    errors={errors}
                    ingredient={ingredient}
                    progress={{ current: 1, total: sectionTotal }}
                    setIngredient={submission.setDraft}
                />
                <IngredientOrganizationSection
                    control={control}
                    ingredient={ingredient}
                    progress={{ current: 2, total: sectionTotal }}
                    setIngredient={submission.setDraft}
                />
                {showSuppliers && (
                    <IngredientSuppliersSection
                        suppliers={ingredient.suppliers}
                        onChange={(suppliers) => submission.setDraft((current) => ({ ...current, suppliers }))}
                        progress={{ current: 3, total: sectionTotal }}
                        showTransport={enabledIngredientStages.length > 0}
                    />
                )}
            </EntityFormPage>
            <EntityUnsavedChangesDialog
                open={leaveGuard.open}
                busy={submission.isSubmitting || upsertIngredient.isLoading}
                onSave={() => {
                    saveForExitRef.current = true;
                    leaveGuard.beginSave();
                    void save();
                }}
                onDiscard={() => {
                    saveForExitRef.current = false;
                    leaveGuard.discard();
                }}
                onKeepEditing={leaveGuard.keepEditing}
            />
        </>
    );
};
